import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Radio, Play, Clock, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

export function IngestPage() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['ingest-status'],
    queryFn: () => api.ingest.status(),
    enabled: !!user,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const { data: adaptersData } = useQuery({
    queryKey: ['ingest-adapters'],
    queryFn: () => api.ingest.adapters(),
    enabled: !!user,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const runMutation = useMutation({
    mutationFn: (adapterId: string) => api.ingest.runNow(adapterId),
    onSuccess: (result) => {
      toast.success(`Fetched ${result.itemsFetched} items (${result.itemsNew} new)`);
      queryClient.invalidateQueries({ queryKey: ['ingest-status'] });
      queryClient.invalidateQueries({ queryKey: ['ingest-adapters'] });
    },
    onError: (err: Error) => toast.error(`Run failed: ${err.message}`),
  });

  const status = statusData;
  const adapters = adaptersData?.adapters ?? [];
  const adapterStatus = status?.adapters ?? {};

  if (!status?.enabled && !statusLoading) {
    return (
      <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
        <PageHeader title="Regulatory Ingest" subtitle="Live regulatory document monitoring" />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Radio className="h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 text-sm font-medium">Ingest Not Enabled</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Set ENABLE_INGEST=true in the environment to activate live regulatory monitoring.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Regulatory Ingest"
        subtitle={`${status?.adapterCount ?? 0} adapters configured`}
      />

      {/* Overall status */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className={cn('h-2 w-2 rounded-full', status?.running ? 'bg-compliance-low animate-pulse' : 'bg-muted-foreground')} />
          <span className="text-muted-foreground">{status?.running ? 'Running' : 'Stopped'}</span>
        </div>
      </div>

      {/* Adapter cards */}
      <div className="space-y-3">
        {Object.entries(adapterStatus).map(([adapterId, stats]) => {
          const health = adapters.find((a: Record<string, unknown>) => a.adapterId === adapterId);
          const typedStats = stats as { lastRun: string | null; fetched: number; newItems: number; errors: number };

          return (
            <Card key={adapterId}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Radio className="h-4 w-4 text-teal" />
                    <div>
                      <p className="text-sm font-semibold">{adapterId}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {health && (
                          <Badge variant="outline" className={cn('text-[10px]',
                            (health as Record<string, unknown>).status === 'healthy' ? 'bg-compliance-low/10 text-compliance-low'
                              : (health as Record<string, unknown>).status === 'degraded' ? 'bg-compliance-medium/10 text-compliance-medium'
                                : 'bg-compliance-critical/10 text-compliance-critical',
                          )}>
                            {(health as Record<string, unknown>).status as string}
                          </Badge>
                        )}
                        {typedStats.lastRun && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Last: {new Date(typedStats.lastRun).toLocaleTimeString()}
                          </span>
                        )}
                        <span>Fetched: {typedStats.fetched}</span>
                        <span>New: {typedStats.newItems}</span>
                        {typedStats.errors > 0 && (
                          <span className="text-compliance-critical">Errors: {typedStats.errors}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => runMutation.mutate(adapterId)}
                    disabled={runMutation.isPending}
                    aria-label={`Run ${adapterId} now`}
                  >
                    {runMutation.isPending ? (
                      <RefreshCw className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Play className="mr-1 h-3.5 w-3.5" />
                    )}
                    Run Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
